import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
  Snackbar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  ArrowBack,
  ExpandMore,
  Save,
  Visibility,
  CloudDone,
  CloudOff,
} from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  contentExists,
  contentMove,
  contentBatchWrite,
  createPreviewPR,
} from "../../services/content";
import { fetchEnums } from "../../services/meta";
import MediaUploader from "../../components/admin/MediaUploader";
import ProductPreview from "../../components/admin/ProductPreview";
import PublishBar from "../../components/admin/PublishBar";
import styled from "styled-components";

const FormSection = styled(Accordion)`
  border: 1px solid #e0e0e0;
  border-radius: 12px !important;
  box-shadow: none !important;
  margin-bottom: 16px !important;

  &::before {
    display: none;
  }

  &.Mui-expanded {
    margin-bottom: 16px !important;
  }
`;

const SectionTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1rem;
`;

const PreviewPanel = styled(Paper)`
  position: sticky;
  top: 24px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e0e0e0;

  @media (max-width: 900px) {
    position: static;
    top: auto;
  }
`;

type ProductForm = {
  slug: string;
  category: string;
  fireRating: string;
  locales: {
    tr: { title: string; subtitle: string; description: string };
    en: { title: string; subtitle: string; description: string };
  };
  media: {
    thumbnail: string;
    images: string[];
    model3d: { url: string; type: "obj" | "stl" | "fbx" } | null;
    catalogPdf: string;
  };
  pricing: {
    showPrice: boolean;
    price?: number;
    currency: string;
  };
};

const defaultForm: ProductForm = {
  slug: "",
  category: "",
  fireRating: "",
  locales: {
    tr: { title: "", subtitle: "", description: "" },
    en: { title: "", subtitle: "", description: "" },
  },
  media: {
    thumbnail: "",
    images: [],
    model3d: null,
    catalogPdf: "",
  },
  pricing: {
    showPrice: false,
    price: undefined,
    currency: "TRY",
  },
};

const normalizeForm = (input: any): ProductForm => {
  const src = input && typeof input === "object" ? input : {};
  const next: any = { ...defaultForm, ...src };

  next.locales = {
    tr: { ...defaultForm.locales.tr, ...(src.locales?.tr || {}) },
    en: { ...defaultForm.locales.en, ...(src.locales?.en || {}) },
  };

  next.media = { ...defaultForm.media, ...(src.media || {}) };
  next.media.images = Array.isArray(src.media?.images) ? [...src.media.images] : [];

  next.pricing = { ...defaultForm.pricing, ...(src.pricing || {}) };
  return next as ProductForm;
};

const ProductEditor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: editSlug } = useParams<{ slug?: string }>();
  const isEditMode = Boolean(editSlug);

  const [form, setForm] = useState<ProductForm>(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [enums, setEnums] = useState<any>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; key?: string }[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "general",
    "content",
    "media",
  ]);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [draftCandidate, setDraftCandidate] = useState<ProductForm | null>(null);
  const [initialRestoreDone, setInitialRestoreDone] = useState(false);

  // Publish bar state
  const [publishMode, setPublishMode] = useState<"prod" | "preview">("prod");
  const [commitMsg, setCommitMsg] = useState("feat(products): save");
  const [branchName, setBranchName] = useState("");
  const [prUrl, setPrUrl] = useState("");

  const tempMediaIdRef = React.useRef(
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );

  const previewBranchRef = React.useRef<string>("");

  const getActiveBranch = () => {
    if (publishMode !== "preview") return undefined;
    const normalized = branchName.trim();
    if (normalized) return normalized;
    if (!previewBranchRef.current) {
      const seed = (form.slug || editSlug || "new").replace(/\s+/g, "-");
      previewBranchRef.current = `preview/${seed}-${Date.now()}`;
    }
    return previewBranchRef.current;
  };

  const activeBranch = getActiveBranch();

  const uploadSlug = isEditMode
    ? editSlug || "temp"
    : `temp/${tempMediaIdRef.current}`;
  const uploadDestPrefix = `data/products/${uploadSlug}`;

  useEffect(() => {
    loadMetadata();
    if (isEditMode && editSlug) {
      loadProduct(editSlug);
    }
  }, [editSlug, isEditMode]);

  useEffect(() => {
    if (initialRestoreDone) return;
    if (isEditMode) {
      setInitialRestoreDone(true);
      return;
    }

    const params = new URLSearchParams(location.search);
    const isClone = params.get("clone") === "true";

    if (isClone) {
      try {
        const raw = localStorage.getItem("productClone");
        if (raw) {
          const data = JSON.parse(raw);
          setForm(normalizeForm(data));
          setHasChanges(true);
        }
      } catch {
      } finally {
        try {
          localStorage.removeItem("productClone");
        } catch {}
      }

      setDraftCandidate(null);
      setInitialRestoreDone(true);
      navigate("/admin/products/new", { replace: true });
      return;
    }

    try {
      const rawDraft = localStorage.getItem("productDraft");
      if (rawDraft) {
        const data = JSON.parse(rawDraft);
        const hasMeaningfulDraft =
          Boolean(data?.slug) ||
          Boolean(data?.locales?.tr?.title) ||
          Boolean(data?.locales?.en?.title) ||
          Boolean(data?.media?.thumbnail) ||
          Boolean(data?.media?.catalogPdf) ||
          Boolean(data?.media?.model3d?.url) ||
          (Array.isArray(data?.media?.images) && data.media.images.length > 0);

        if (hasMeaningfulDraft) {
          setDraftCandidate(normalizeForm(data));
        }
      }
    } catch {}

    setInitialRestoreDone(true);
  }, [initialRestoreDone, isEditMode, location.search, navigate]);

  // Autosave draft to localStorage
  useEffect(() => {
    if (!hasChanges) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem("productDraft", JSON.stringify(form));
      } catch {}
    }, 1000);
    return () => clearTimeout(timer);
  }, [form, hasChanges]);

  const loadMetadata = async () => {
    try {
      const e = await fetchEnums();
      setEnums(e);
    } catch {}

    try {
      const res = await fetch("/data/categories/index.json", { cache: "no-store" });
      const json = await res.json();
      setCategoryOptions(Array.isArray(json.items) ? json.items : []);
    } catch {
      setCategoryOptions([]);
    }
  };

  const loadProduct = async (slug: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/data/products/${slug}/index.json`, { cache: "no-store" });
      if (!res.ok) throw new Error("Ürün bulunamadı");
      const json = await res.json();
      setForm(normalizeForm(json));
      setHasChanges(false);
    } catch (e: any) {
      setError(e?.message || "Ürün yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/ş/g, "s")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/ı/g, "i")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/(^-|-$)/g, "");

  const handleSectionToggle = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const updateForm = (path: string, value: any) => {
    setDraftCandidate((prev) => (prev ? null : prev));
    setHasChanges(true);
    setForm((prev) => {
      const keys = path.split(".");
      const next = { ...prev };
      let cur: any = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const pathHasPrefix = (p: string, prefix: string) => {
    if (!p) return false;
    const withSlash = prefix.startsWith("/") ? prefix : `/${prefix}`;
    const withoutSlash = withSlash.replace(/^\/+/, "");
    return p.startsWith(withSlash) || p.startsWith(withoutSlash);
  };

  const replacePathPrefix = (p: string, fromPrefix: string, toPrefix: string) => {
    if (!p) return p;
    const fromWithSlash = fromPrefix.startsWith("/") ? fromPrefix : `/${fromPrefix}`;
    const toWithSlash = toPrefix.startsWith("/") ? toPrefix : `/${toPrefix}`;
    if (p.startsWith(fromWithSlash)) {
      return `${toWithSlash}${p.slice(fromWithSlash.length)}`;
    }

    const fromWithoutSlash = fromWithSlash.replace(/^\/+/, "");
    const toWithoutSlash = toWithSlash.replace(/^\/+/, "");
    if (p.startsWith(fromWithoutSlash)) {
      return `${toWithoutSlash}${p.slice(fromWithoutSlash.length)}`;
    }

    return p;
  };

  const rewriteMediaPrefix = (
    src: ProductForm,
    fromPrefix: string,
    toPrefix: string
  ): ProductForm => {
    const media = src.media || defaultForm.media;
    return {
      ...src,
      media: {
        ...media,
        thumbnail: replacePathPrefix(media.thumbnail, fromPrefix, toPrefix),
        images: (media.images || []).map((img) =>
          replacePathPrefix(img, fromPrefix, toPrefix)
        ),
        model3d: media.model3d
          ? { ...media.model3d, url: replacePathPrefix(media.model3d.url, fromPrefix, toPrefix) }
          : null,
        catalogPdf: replacePathPrefix(media.catalogPdf, fromPrefix, toPrefix),
      },
    };
  };

  const hasMediaWithPrefix = (src: ProductForm, prefix: string) => {
    const media = src.media || defaultForm.media;
    if (pathHasPrefix(media.thumbnail, prefix)) return true;
    if ((media.images || []).some((img) => pathHasPrefix(img, prefix))) return true;
    if (media.model3d?.url && pathHasPrefix(media.model3d.url, prefix)) return true;
    if (pathHasPrefix(media.catalogPdf, prefix)) return true;
    return false;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      // Validation
      if (!form.locales?.tr?.title && !form.locales?.en?.title) {
        throw new Error("En az bir dilde başlık gerekli");
      }

      const slug =
        form.slug?.trim() ||
        generateSlug(form.locales?.tr?.title || form.locales?.en?.title || "");
      if (!slug) throw new Error("Slug gerekli");
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
        throw new Error("Slug sadece küçük harf, rakam ve tire içermelidir");
      }

      if (
        form.pricing?.showPrice &&
        (form.pricing?.price === undefined || Number.isNaN(Number(form.pricing?.price)))
      ) {
        throw new Error("Fiyat gerekli");
      }

      // Check slug uniqueness for new products
      if (!isEditMode) {
        const exists = await contentExists(`data/products/${slug}/index.json`);
        if (exists) throw new Error("Bu slug zaten mevcut");
      }

      const branch = activeBranch;

      let finalForm = { ...form, slug };

      if (!isEditMode) {
        const tempPrefix = `/data/products/temp/${tempMediaIdRef.current}/`;
        if (hasMediaWithPrefix(finalForm, tempPrefix)) {
          finalForm = rewriteMediaPrefix(
            finalForm,
            tempPrefix,
            `/data/products/${slug}/`
          );
          await contentMove(
            `data/products/temp/${tempMediaIdRef.current}`,
            `data/products/${slug}`,
            branch
          );
        }
      }

      if (isEditMode && editSlug && slug !== editSlug) {
        const existsNew = await contentExists(`data/products/${slug}/index.json`);
        if (existsNew) throw new Error("Bu slug başka bir ürün tarafından kullanılıyor");

        finalForm = rewriteMediaPrefix(
          finalForm,
          `/data/products/${editSlug}/`,
          `/data/products/${slug}/`
        );

        await contentMove(
          `data/products/${editSlug}`,
          `data/products/${slug}`,
          branch
        );
      }

      // Build summary
      const summaryRes = await fetch("/data/products/index.json", { cache: "no-store" });
      const summary = await summaryRes.json();
      const itemsArray = Array.isArray(summary.items) ? summary.items : [];

      const image = finalForm.media?.thumbnail || finalForm.media?.images?.[0] || "";
      const summaryItem = {
        id: itemsArray.length ? Math.max(...itemsArray.map((i: any) => i.id)) + 1 : 1,
        title: finalForm.locales?.tr?.title || finalForm.locales?.en?.title || slug,
        image,
        fireRating: finalForm.fireRating || "",
        category: finalForm.category || "",
        slug,
        price: finalForm.pricing?.price,
        showPrice: Boolean(finalForm.pricing?.showPrice),
        model3d: finalForm.media?.model3d || undefined,
      };

      const existingIndex = itemsArray.findIndex((i: any) =>
        isEditMode ? i.slug === editSlug : i.slug === slug
      );
      if (existingIndex >= 0) {
        itemsArray[existingIndex] = { ...itemsArray[existingIndex], ...summaryItem, id: itemsArray[existingIndex].id };
      } else {
        itemsArray.push(summaryItem);
      }

      // Write both files
      await contentBatchWrite(
        [
          { path: `data/products/${slug}/index.json`, textContent: JSON.stringify(finalForm, null, 2) },
          { path: "data/products/index.json", textContent: JSON.stringify({ items: itemsArray }, null, 2) },
        ],
        `${commitMsg} ${slug}`,
        branch
      );

      if (branch) {
        try {
          const pr = await createPreviewPR(branch, `Content Update: ${slug}`);
          setPrUrl(pr.url);
        } catch {
        }
      }

      setSuccess("Ürün kaydedildi");
      setLastSaved(new Date());
      setHasChanges(false);

      // Clear draft
      try {
        localStorage.removeItem("productDraft");
      } catch {}

      // Navigate to list after short delay
      setTimeout(() => {
        navigate("/admin/products");
      }, 1500);
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate("/admin/products")}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            Ürünler
          </Link>
          <Typography color="text.primary">
            {isEditMode ? "Düzenle" : "Yeni Ürün"}
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a1a1a" }}>
              {isEditMode ? form.locales?.tr?.title || "Ürünü Düzenle" : "Yeni Ürün Ekle"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              {hasChanges ? (
                <Chip
                  icon={<CloudOff sx={{ fontSize: 16 }} />}
                  label="Kaydedilmemiş değişiklikler"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ) : lastSaved ? (
                <Chip
                  icon={<CloudDone sx={{ fontSize: 16 }} />}
                  label={`Son kayıt: ${lastSaved.toLocaleTimeString("tr-TR")}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : null}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <PublishBar
              commitMsg={commitMsg}
              setCommitMsg={setCommitMsg}
              publishMode={publishMode}
              setPublishMode={setPublishMode}
              branchName={branchName}
              setBranchName={setBranchName}
              branchPlaceholder={`preview/${form.slug || "new"}`}
            />
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 140 }}
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {!isEditMode && !hasChanges && draftCandidate && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setForm(draftCandidate);
                  setHasChanges(true);
                  setDraftCandidate(null);
                }}
              >
                Taslağı Yükle
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  try {
                    localStorage.removeItem("productDraft");
                  } catch {}
                  setDraftCandidate(null);
                  setForm(defaultForm);
                  setHasChanges(false);
                }}
              >
                Sil
              </Button>
            </Stack>
          }
        >
          Taslak bulundu. Devam etmek ister misiniz?
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Form Column */}
        <Grid item xs={12} lg={8}>
          {/* General Section */}
          <FormSection
            expanded={expandedSections.includes("general")}
            onChange={() => handleSectionToggle("general")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <SectionTitle>Genel Bilgiler</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Slug (URL)"
                    fullWidth
                    value={form.slug}
                    onChange={(e) => updateForm("slug", e.target.value)}
                    helperText="Otomatik oluşturulur veya manuel girin"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Kategori"
                    select
                    fullWidth
                    value={categoryOptions.find((opt) => opt.key === form.category)?.value || form.category}
                    onChange={(e) => updateForm("category", e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="">Seçiniz</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt.key || opt.value} value={opt.value}>
                        {opt.value}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Yangın Sınıfı"
                    select
                    fullWidth
                    value={form.fireRating}
                    onChange={(e) => updateForm("fireRating", e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="">Seçiniz</option>
                    {(enums?.fireClass?.items || []).map((item: any) => (
                      <option key={item.value} value={item.value}>
                        {item.labels?.tr || item.value}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </FormSection>

          {/* Content Section - TR */}
          <FormSection
            expanded={expandedSections.includes("content")}
            onChange={() => handleSectionToggle("content")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <SectionTitle>İçerik (Türkçe / English)</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    🇹🇷 Türkçe
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Başlık"
                      fullWidth
                      value={form.locales.tr.title}
                      onChange={(e) => updateForm("locales.tr.title", e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Alt Başlık"
                      fullWidth
                      value={form.locales.tr.subtitle}
                      onChange={(e) => updateForm("locales.tr.subtitle", e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Açıklama"
                      fullWidth
                      multiline
                      rows={4}
                      value={form.locales.tr.description}
                      onChange={(e) => updateForm("locales.tr.description", e.target.value)}
                      size="small"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    🇬🇧 English
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Title"
                      fullWidth
                      value={form.locales.en.title}
                      onChange={(e) => updateForm("locales.en.title", e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Subtitle"
                      fullWidth
                      value={form.locales.en.subtitle}
                      onChange={(e) => updateForm("locales.en.subtitle", e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      rows={4}
                      value={form.locales.en.description}
                      onChange={(e) => updateForm("locales.en.description", e.target.value)}
                      size="small"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </FormSection>

          {/* Media Section */}
          <FormSection
            expanded={expandedSections.includes("media")}
            onChange={() => handleSectionToggle("media")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <SectionTitle>Medya</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Kapak Görseli
                  </Typography>
                  <MediaUploader
                    title="Kapak yükle"
                    accept="image/*"
                    multiple={false}
                    destPrefix={uploadDestPrefix}
                    branch={activeBranch}
                    onUploaded={(paths) => updateForm("media.thumbnail", paths[0] || "")}
                  />
                  {form.media.thumbnail && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={form.media.thumbnail.split("/").pop()}
                        onDelete={() => updateForm("media.thumbnail", "")}
                        size="small"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Galeri Görselleri
                  </Typography>
                  <MediaUploader
                    title="Görseller yükle"
                    accept="image/*"
                    multiple
                    destPrefix={uploadDestPrefix}
                    branch={activeBranch}
                    onUploaded={(paths) =>
                      updateForm("media.images", [...form.media.images, ...paths])
                    }
                  />
                  {form.media.images.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {form.media.images.map((img, idx) => (
                        <Chip
                          key={idx}
                          label={img.split("/").pop()}
                          onDelete={() =>
                            updateForm(
                              "media.images",
                              form.media.images.filter((_, i) => i !== idx)
                            )
                          }
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    3D Model & Katalog
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MediaUploader
                        title="3D Model (OBJ/STL/FBX)"
                        accept=".obj,.stl,.fbx"
                        multiple={false}
                        destPrefix={uploadDestPrefix}
                        branch={activeBranch}
                        onUploaded={(paths) => {
                          if (paths[0]) {
                            const ext = paths[0].split(".").pop()?.toLowerCase();
                            updateForm("media.model3d", {
                              url: paths[0],
                              type: ext === "stl" ? "stl" : ext === "fbx" ? "fbx" : "obj",
                            });
                          }
                        }}
                      />
                      {form.media.model3d?.url && (
                        <Chip
                          label={form.media.model3d.url.split("/").pop()}
                          onDelete={() => updateForm("media.model3d", null)}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MediaUploader
                        title="Katalog PDF"
                        accept=".pdf"
                        multiple={false}
                        destPrefix={uploadDestPrefix}
                        branch={activeBranch}
                        onUploaded={(paths) => updateForm("media.catalogPdf", paths[0] || "")}
                      />
                      {form.media.catalogPdf && (
                        <Chip
                          label={form.media.catalogPdf.split("/").pop()}
                          onDelete={() => updateForm("media.catalogPdf", "")}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </FormSection>

          {/* Pricing Section */}
          <FormSection
            expanded={expandedSections.includes("pricing")}
            onChange={() => handleSectionToggle("pricing")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <SectionTitle>Fiyatlandırma</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.pricing.showPrice}
                        onChange={(e) => updateForm("pricing.showPrice", e.target.checked)}
                      />
                    }
                    label="Fiyatı göster"
                  />
                </Grid>
                {form.pricing.showPrice && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Fiyat"
                        type="number"
                        fullWidth
                        value={form.pricing.price ?? ""}
                        onChange={(e) =>
                          updateForm(
                            "pricing.price",
                            e.target.value === "" ? undefined : Number(e.target.value)
                          )
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Para Birimi"
                        select
                        fullWidth
                        value={form.pricing.currency}
                        onChange={(e) => updateForm("pricing.currency", e.target.value)}
                        SelectProps={{ native: true }}
                        size="small"
                      >
                        <option value="TRY">TRY (₺)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                      </TextField>
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </FormSection>
        </Grid>

        {/* Preview Column */}
        <Grid item xs={12} lg={4}>
          <PreviewPanel elevation={0}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Visibility sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Canlı Önizleme
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              <ProductPreview form={form} branch={activeBranch} />
            </Box>
          </PreviewPanel>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess("")}>
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
          {prUrl && (
            <Button component="a" href={prUrl} target="_blank" size="small" sx={{ ml: 1 }}>
              PR'ı Aç
            </Button>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductEditor;
